(ns app.front.view.application.application-reports
  (:require [app.domain.attachment.file-formats :as file-formats]
            [app.domain.planning-report.model :as planning-report-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.typography :refer [p]]
            [app.front.util :as futil]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))


(def new-uploaded-report-id "new-planning-uploaded-report")


(defn get-reports [application]
  (->> (:entity/_parent application)
       (filter planning-report-model/report?)
       (sort-by :planning-report/published)))


(defn create-button [{:keys [signal application tr]}]
  (when (<sub [:app.front.process/app-signal-permitted? [signal]])
    (cc/button {:kind             :ghost
                :on-click         #(>evt [::process/signal application [signal]])
                :icon             i/plus
                :icon-description ""}
               (tr [signal]))))


(defn create-report-button
  "Adds a button for creating draft or final reports (not uploaded ones)"
  [state]
  [:<>
   [create-button (assoc state :signal :process.signal/create-draft-report)]
   [create-button (assoc state :signal :process.signal/create-final-report)]])


(defn- initialize-new-uploaded-report
  "Sets the data to unsaved changes so the user can edit the description and date before saving the report"
  [file-id filename content-type md5]
  (>evt [::ee/set-values new-uploaded-report-id {:planning-uploaded-report/description ""
                                                 :planning-uploaded-report/report-date (js/Date.)
                                                 :temp-file-data                       {:file-id           file-id
                                                                                        :file/category     :file.category/report
                                                                                        :file/filename     filename
                                                                                        :file/content-type content-type
                                                                                        :file/md5          md5}}]))


(defn- create-uploaded-report
  "Sends the uploaded report file and associated information to the backend"
  [fsm-target]
  (let [get-val     #(<sub [::ee/attr-value new-uploaded-report-id %])
        description (get-val :planning-uploaded-report/description)
        report-date (get-val :planning-uploaded-report/report-date)
        file-data   (get-val :temp-file-data)]
    (>evt [:app.front.process/signal
           fsm-target
           [:process.signal/add-uploaded-report
            (merge (util/mmap description report-date) file-data)]
           {:notify {:success :planning-reports/upload-success}}])))


(defn- permitted? [fsm-target signal-id]
  (<sub [::process/signal-permitted? fsm-target [signal-id]]))


(defn- upload-button [on-attach]
  [upload/upload-button
   "upload-report"
   {:attach-file-fn  on-attach
    :button-i18n-key :planning-reports/upload-help
    :accept-types    file-formats/convertable-accept-value-for-input}])


(defn- report-list-item [{:keys [class]} & children]
  (into [:li.p-4.mb-4.grid.gap-y-4.md:gap-y-0.bg-ui-light-gray.border-l-4.border-solid
         {:class class}]
        children))


(defn- pdf-link [{:keys [tr report]}]
  (when-let [pdf (<sub [::entities/entity {:entity/type   :entity.type/file
                                           :entity/parent (-> report :db/id)}])]
    [file-download/download-workspace-file-link pdf
     {:text (tr [:planning-reports/download])}]))


(defn- template-report-item
  "Final or draft report list item"
  [{:keys [tr workspace report]}]
  (let [published?       (:planning-report/published report)
        is-draft-report? (eu/is-type? :entity.type/planning-draft-report report)
        title            (if is-draft-report?
                           :planning-reports/draft-report
                           :planning-reports/final-report)
        view             (if is-draft-report?
                           :view/planning-draft-report
                           :view/planning-final-report)
        status           (if published?
                           (tr [:planning-reports/published])
                           (tr [:planning-reports/not-published]))
        class            (futil/cs (if published? "border-ui-green" "border-ui-yellow")
                                   "items-center grid-cols-2 sm:grid-cols-4")
        workspace-id     (:workspace/id workspace)]
    [report-list-item (util/mmap class)
     [:h2.m-0.text-sm.leading-tight (tr [title])]
     [p "font-bold uppercase text-right md:text-left" status]
     [:div.flex.items-center
      [pdf-link (util/mmap tr report)]]
     [:div.flex.justify-end.items-center
      (cc/link {:class-name "flex items-center ml-6"
                :href       (href/href view {:workspace-id workspace-id
                                             :report-id    (:db/id report)})}
               (tr [:open])
               (cc/icon {:icon        i/arrow-right
                         :class-name  "ml-4"
                         :height      "18px"
                         :fill        "var(--ui-blue)"
                         :description ""}))]]))


(defn- uploaded-report-item
  "Uploaded report item"
  [{:keys [tr fsm-target report on-save]}]
  (let [report-id   (or (:db/id report) new-uploaded-report-id)
        adding-new? (-> report :db/id nil?)
        editing?    (<sub [::ee/has-changes? report-id])
        description (<sub [::ee/attr-value report-id :planning-uploaded-report/description])
        report-date (<sub [::ee/attr-value report-id :planning-uploaded-report/report-date])
        class       (futil/cs (if adding-new? "border-ui-yellow" "border-ui-green")
                              (if editing? "items-start" "items-center sm:grid-cols-2 md:grid-cols-5")
                              "grid-cols-1")]
    [report-list-item (util/mmap tr class)
     [:div.md:col-span-2
      ;; Description
      (if editing?
        [ui/text-area
         {:id         (str "planning-report-desc-" report-id)
          :label-text (tr [:planning-reports/description])
          :light      true
          :value      description
          :on-change  #(rf/dispatch-sync
                         [::ee/set-value report-id :planning-uploaded-report/description (futil/tval %)])}]
        description)]
     ;; Report date
     (if editing?
       [ui/date-picker
        {:label-text (tr [:planning-reports/report-date])
         :value      report-date
         :class-name "md:mt-4"
         :callback   #(>evt [::ee/set-value report-id :planning-uploaded-report/report-date %])}]
       ((<sub [::date-util/date-fn]) report-date))
     ;; Buttons & link
     (if editing?
       [:div.flex.justify-end.h-full.md:items-end
        (cc/button
          {:kind     :tertiary
           :on-click #(>evt [::ee/cancel-changes report-id])}
          (tr [:cancel]))
        (cc/button
          {:kind     :primary
           :on-click #(do (when on-save (on-save))
                          (if adding-new?
                            (>evt [::ee/cancel-changes report-id])
                            (>evt [::ee/save-changes fsm-target :process.signal/edit-uploaded-report report-id])))}
          (tr [:save]))]
       [:<>
        [:div [pdf-link (util/mmap tr report)]]
        [:div.flex.flex-row.justify-end
         (when (permitted? fsm-target :process.signal/edit-uploaded-report)
           [cc/icon
            {:icon        i/pen
             :description (tr [:edit])
             :class-name  "cursor-pointer"
             :on-click    #(>evt [::ee/set-values
                                  report-id
                                  (select-keys report [:planning-uploaded-report/description
                                                       :planning-uploaded-report/report-date])])}])
         (when (permitted? fsm-target :process.signal/delete-uploaded-report)
           [ui/modal-confirmation-button
            {:tr                tr
             :id                (str "planning-report-remove-" report-id)
             :modal-class       "text-left"
             :modal-title       (tr [:planning-reports/confirm-remove])
             :on-yes-click      #(>evt [:app.front.process/signal
                                        fsm-target
                                        [:process.signal/delete-uploaded-report {:report-id report-id}]
                                        {:notify {:success :planning-reports/delete-success}}])
             :trigger-icon-opts {:icon        i/trash
                                 :description (tr [:remove])
                                 :class-name  "ml-4 cursor-pointer"}}])]])]))


(defn- report-list
  "Reports generated in Cloudpermit using the draft and final report templates"
  [{:keys [tr reports] :as opts}]
  (if (empty? reports)
    [p "mb-4" (tr [:planning-reports/no-reports])]
    [:ul
     (futil/for-frag [report reports
                      :let   [opts (assoc opts :report report)]]
       (if (eu/is-type? :entity.type/planning-uploaded-report report)
         [uploaded-report-item opts]
         [template-report-item opts]))]))


(defn reports
  "The 'Reports' section in the workspace"
  [state]
  (let [{:keys [application
                workspace
                tr]}        state
        fsm-target          application
        can-upload?         (permitted? fsm-target :process.signal/add-uploaded-report)
        creating-new?       (<sub [::ee/has-changes? new-uploaded-report-id])
        reports             (get-reports application)]
    [section/initially-collapsed-section
     :application-reports
     (cond-> state
       (not can-upload?) (assoc :heading-button [create-report-button state]))
     :view/application-reports
     [report-list (util/mmap tr workspace fsm-target reports)]
     (if creating-new?
       [uploaded-report-item (util/mmap {:on-save (partial create-uploaded-report fsm-target)}
                                        tr fsm-target)]
       (when can-upload?
         [:div.bx--row
          [:div.bx--col-xs-12
           [upload-button initialize-new-uploaded-report]]]))]))
